/* eslint-disable no-nested-ternary */
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import DataTable from "components/DataTable";
import helpers from "../AnalyticsHelpers";

function AnalyticsTable({
  points,
  queryPeriod,
  measurement,
  field,
  projectTimeZone,
  metricsConfig,
  t,
}) {
  const hasApartment = points.some((point) => point?.apartment);

  const effectiveSuffix = helpers.getMetricSuffix(measurement, metricsConfig);

  const isIncremental = helpers.isIncrementalMetric(measurement);
  const isDiffIncrementalHourly = isIncremental && field === "diff" && queryPeriod === "hourly";

  const columns = useMemo(() => {
    const totalColumns = hasApartment
      ? isDiffIncrementalHourly
        ? 4
        : 3
      : isDiffIncrementalHourly
      ? 3
      : 2;
    const equalWidth = `${100 / totalColumns}%`;

    const cols = [
      { Header: t("date"), accessor: "date", align: "center", width: equalWidth },
      ...(hasApartment
        ? [
            {
              Header: t("apartment"),
              accessor: "apartment",
              align: "center",
              width: equalWidth,
            },
          ]
        : []),
      { Header: t("value"), accessor: "value", align: "center", width: equalWidth },
      ...(isDiffIncrementalHourly
        ? [{ Header: t("state"), accessor: "state", align: "center", width: equalWidth }]
        : []),
    ];
    return cols;
  }, [hasApartment, isDiffIncrementalHourly, t]);

  const rows = useMemo(
    () =>
      points.map((row) => {
        const dateStr = helpers.formatAnalyticsDate({
          ts: row?.timestamp,
          queryPeriod,
          timeZone: projectTimeZone,
        });
        const valStr = helpers.formatValueToTable(
          row?.value,
          measurement,
          field,
          effectiveSuffix,
          t
        );

        return {
          date: dateStr,
          apartment: hasApartment ? row?.apartment ?? row?.label ?? "" : undefined,
          value: valStr,
          state: isDiffIncrementalHourly ? row?.state ?? "" : undefined,
        };
      }),
    [
      points,
      hasApartment,
      queryPeriod,
      measurement,
      field,
      effectiveSuffix,
      isDiffIncrementalHourly,
    ]
  );

  const tableData = useMemo(() => ({ columns, rows }), [columns, rows]);

  return (
    <MDBox mt={2}>
      <DataTable
        table={tableData}
        entriesPerPage={
          tableData.rows.length > 10 ? { defaultValue: 10, entries: [5, 10, 15, 20, 25] } : false
        }
        showTotalEntries={tableData.rows.length > 10}
        canSearch={tableData.rows.length > 10}
        isSorted={false}
        pagination={{ variant: "gradient", color: "info" }}
        noEndBorder
      />
    </MDBox>
  );
}

AnalyticsTable.propTypes = {
  points: PropTypes.array.isRequired,
  queryPeriod: PropTypes.string,
  measurement: PropTypes.string,
  field: PropTypes.string,
  projectTimeZone: PropTypes.string,
  metricsConfig: PropTypes.object,
  t: PropTypes.func,
};

AnalyticsTable.defaultProps = {
  queryPeriod: undefined,
  measurement: "",
  field: "",
  projectTimeZone: "UTC",
  metricsConfig: {},
  t: () => {},
};

export default AnalyticsTable;
